/**
 * Hook for managing widget state and operations
 * Handles adding, resizing, and visibility of widgets
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import { WidgetConfig, DashboardWidget } from '../types'
import { GridSize } from '../utils/grid-size-utils'
import { getWidgetById } from '../widget-registry'

interface UseWidgetManagementProps {
    initialWidgets: WidgetConfig[]
    loadSavedLayout: () => DashboardWidget[]
    saveLayout: (widgets: DashboardWidget[]) => void
}

interface UseWidgetManagementReturn {
    dashboardWidgets: DashboardWidget[]
    setDashboardWidgets: React.Dispatch<React.SetStateAction<DashboardWidget[]>>
    activeWidgetIds: string[]
    initialWidgetIds: string[]
    handleAddWidget: (widgetId: string) => void
    handleResize: (id: string, newColSpan: GridSize) => void
}

export function useWidgetManagement({
    initialWidgets,
    loadSavedLayout,
    saveLayout,
}: UseWidgetManagementProps): UseWidgetManagementReturn {
    const [dashboardWidgets, setDashboardWidgets] =
        useState<DashboardWidget[]>(loadSavedLayout)

    // Update widgets when initialWidgets changes
    useEffect(() => {
        setDashboardWidgets(loadSavedLayout())
    }, [initialWidgets.length, loadSavedLayout])

    // Get active widget IDs for the store (visible widgets)
    const activeWidgetIds = useMemo(() => {
        return dashboardWidgets.filter((w) => w.isVisible).map((w) => w.id)
    }, [dashboardWidgets])

    // Get all initial widget IDs (widgets that have actual components)
    const initialWidgetIds = useMemo(() => {
        return initialWidgets.map((w) => w.id)
    }, [initialWidgets])

    // Add widget from store
    const handleAddWidget = useCallback(
        (widgetId: string) => {
            // Check if widget exists in initial widgets
            const existingWidget = initialWidgets.find((w) => w.id === widgetId)

            if (existingWidget) {
                // Widget exists but is hidden - just make it visible
                setDashboardWidgets((items) => {
                    const widget = items.find((w) => w.id === widgetId)
                    if (widget) {
                        const updatedWidgets = items.map((item) =>
                            item.id === widgetId
                                ? { ...item, isVisible: true }
                                : item
                        )
                        saveLayout(updatedWidgets)
                        return updatedWidgets
                    }
                    return items
                })
            } else {
                // Widget from registry - add as new
                const widgetDef = getWidgetById(widgetId)
                if (widgetDef) {
                    setDashboardWidgets((items) => {
                        const newWidget: DashboardWidget = {
                            id: widgetId,
                            component: null, // Component will be provided by parent
                            colSpan: widgetDef.defaultColSpan,
                            order: items.length,
                            isVisible: true,
                        }
                        const updatedWidgets = [...items, newWidget]
                        saveLayout(updatedWidgets)
                        return updatedWidgets
                    })
                }
            }
        },
        [initialWidgets, saveLayout]
    )

    // Handle resize
    const handleResize = useCallback(
        (id: string, newColSpan: GridSize) => {
            setDashboardWidgets((items) => {
                const updatedWidgets = items.map((item) =>
                    item.id === id ? { ...item, colSpan: newColSpan } : item
                )
                saveLayout(updatedWidgets)
                return updatedWidgets
            })
        },
        [saveLayout]
    )

    return {
        dashboardWidgets,
        setDashboardWidgets,
        activeWidgetIds,
        initialWidgetIds,
        handleAddWidget,
        handleResize,
    }
}
